import { createSelector } from '@reduxjs/toolkit';
import { urls } from '@ff-os/shared-utils/urls';
import { selectAllSizeIds, selectAllSizes } from '@ff-scene-plugins/photo-prints-redux/selectors/sizeSelectors';
import { selectAllVariants, selectPrintQuality, selectVariantById } from '@ff-scene-plugins/photo-prints-redux/selectors/variantSelectors';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { findVariant, skuFor } from '@ff-scene-plugins/photo-prints-shared/variants';
import { selectDefaultBorderId } from './_redux-borders';
import { selectDefaultPaperId } from './_redux-papers';
import { selectVariantByPhotoId } from './_redux-variants';
import { CompatibleWithRule, VariantOptionWithPriceRequest } from './VariantOption';

const selectSizeIdsForPaper = createSelector([
    selectAllVariants,
    (state: PhotoPrintsPdpState, paperId: string | undefined) => paperId
], (variants, paperId) => {
    return variants
        .filter(v => paperId === undefined || v.paperId === paperId)
        .map(v => v.sizeId);
});

const selectCompatiblePaperIdsBySizeId = createSelector([
    selectAllSizes,
    selectAllVariants
], (sizes, variants) => {
    const compatiblePaperIdsBySizeId = {} as Record<string, CompatibleWithRule[]>;
    for (const size of sizes) {
        compatiblePaperIdsBySizeId[size.id] = variants
            .filter(v => v.sizeId === size.id)
            .map(v => ({paperId: v.paperId}));
    }
    return compatiblePaperIdsBySizeId;
});

const selectSkuBySizeId = createSelector([
    selectAllSizeIds,
    selectAllVariants,
    selectDefaultPaperId,
    selectDefaultBorderId,
    selectPrintQuality,
    (state: PhotoPrintsPdpState, paperId?: string) => paperId,
    (state: PhotoPrintsPdpState, paperId?: string, borderId?: string) => borderId
], (sizeIds, variants, defaultPaperId, defaultBorderId, quality, chosenPaperId, chosenBorderId) => {

    const skuBySizeId = {} as Record<string, string>;

    for (const sizeId of sizeIds) {

        const variantBySizeIdAndChosenPaperId = findVariant(variants,
            { borderId: chosenBorderId, paperId: chosenPaperId, sizeId });

        if (variantBySizeIdAndChosenPaperId) {
            skuBySizeId[sizeId] = skuFor(variantBySizeIdAndChosenPaperId, quality);
        } else {
            const defaultVariant = findVariant(variants,
                { borderId: defaultBorderId, paperId: defaultPaperId, sizeId });

            skuBySizeId[sizeId] = defaultVariant ? skuFor(defaultVariant, quality) : '';
        }
    }

    return skuBySizeId;
});

export const selectSizeOptions = createSelector([
    selectAllSizes,
    selectSizeIdsForPaper,
    selectCompatiblePaperIdsBySizeId,
    selectSkuBySizeId
], (allSizes, availableSizeIds, compatiblePaperIdsBySizeId, skuBySizeId) => {
    return allSizes.map(s => toVariantOption(s, availableSizeIds, compatiblePaperIdsBySizeId, skuBySizeId));
});

export const selectSizeByPhotoId = createSelector([
    selectVariantByPhotoId,
    selectAllSizes
], (variant, sizes) => {
    return sizes.find(s => s.id === variant.sizeId)!;
});

const selectPaperIdByVariantId = createSelector([
    selectVariantById
], variant => variant!.paperId);

const selectSizesOptionsByVariantId = createSelector([
    state => state,
    selectPaperIdByVariantId
], selectSizeOptions);

export const selectActiveSizeOptionForVariantId = createSelector([
    selectVariantById,
    selectSizesOptionsByVariantId
], (variant, sizes) => sizes.find(s => s.value === variant!.sizeId)!);

const toVariantOption = (
    size: PhotoPrintsJson.Size,
    availableSizeIds: string[],
    compatiblePaperIdsBySizeId: Record<string, CompatibleWithRule[]>,
    skuBySizeId: Record<string, string>
): VariantOptionWithPriceRequest => ({
    value: size.id,
    name: {
        key: size.name
    },
    label: {
        key: size.label,
        args: {
            widthInCm: size.width / 10,
            heightInCm: size.height / 10
        }
    },
    iconUrl: urls.publicAssetUrl(size.iconAssetId),
    compatibleWith: compatiblePaperIdsBySizeId[size.id],
    available: availableSizeIds.includes(size.id),
    isAccessory: size.isAccessory ?? false,
    price: undefined,
    priceRequest: {
        sku: skuBySizeId[size.id]
    }
});